import { useState } from "react";
import { ArrowRight, Check, Download, FileText } from "lucide-react";
import { toast } from "sonner";
import { submitReportLead, reportDownloadUrl, reportFileUrl, trackEvent } from "../../lib/api";

export const ReportDownloadButton = ({ reportSlug, pdfFilename }) => (
  <div data-testid="report-direct-download" className="text-white">
    <div className="flex items-center gap-2 text-sm font-semibold text-white">
      <FileText className="h-5 w-5 text-brand-red" /> Free PDF — no sign-up required
    </div>
    <p className="mt-2 text-sm text-white/60">
      Download the full report instantly. No email or contact details needed.
    </p>
    <a
      href={reportFileUrl(reportSlug)}
      rel="noopener"
      download={pdfFilename || ""}
      data-testid="report-download-button"
      onClick={() => trackEvent("report_download", reportSlug)}
      className="mt-4 inline-flex w-full items-center justify-center gap-2 bg-brand-red px-5 py-3 text-sm font-semibold text-white transition-all hover:-translate-y-px hover:brightness-110"
    >
      <Download className="h-4 w-4" /> Download Report (PDF)
    </a>
  </div>
);

export const ReportLeadForm = ({ reportSlug, sourcePage, pdfFilename }) => {
  const [form, setForm] = useState({ name: "", company: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.company || !form.email) return;
    setLoading(true);
    try {
      const { download_url } = await submitReportLead({
        ...form,
        report_slug: reportSlug,
        source_page: sourcePage,
      });
      const full = reportDownloadUrl(download_url);
      setDownloadUrl(full);
      trackEvent("report_lead", reportSlug);
      toast.success("Thank you — your report is ready to download.");
      // Auto-start the download.
      const a = document.createElement("a");
      a.href = full;
      a.rel = "noopener";
      a.download = pdfFilename || "";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (downloadUrl) {
    return (
      <div data-testid="report-download-ready" className="text-white">
        <div className="flex items-center gap-2 text-sm font-semibold text-white">
          <Check className="h-5 w-5 text-emerald-400" /> Your download has started.
        </div>
        <p className="mt-2 text-sm text-white/60">
          If it did not begin automatically, use the button below. The link is valid for a short time.
        </p>
        <a
          href={downloadUrl}
          rel="noopener"
          data-testid="report-download-button"
          className="mt-4 inline-flex w-full items-center justify-center gap-2 bg-brand-red px-5 py-3 text-sm font-semibold text-white transition-all hover:-translate-y-px hover:brightness-110"
        >
          <Download className="h-4 w-4" /> Download Report (PDF)
        </a>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="report-lead-form" className="space-y-3">
      <div className="flex items-center gap-2 text-sm font-semibold text-white">
        <FileText className="h-5 w-5 text-brand-red" /> Download the full report
      </div>
      <p className="text-sm text-white/60">
        Enter your details to receive the full PDF. No account required.
      </p>
      <input
        required
        value={form.name}
        onChange={set("name")}
        placeholder="Name"
        data-testid="report-lead-name"
        className="h-11 w-full border border-white/20 bg-white/5 px-3 text-sm text-white outline-none placeholder:text-white/40 focus:ring-2 focus:ring-brand-blue"
      />
      <input
        required
        value={form.company}
        onChange={set("company")}
        placeholder="Company"
        data-testid="report-lead-company"
        className="h-11 w-full border border-white/20 bg-white/5 px-3 text-sm text-white outline-none placeholder:text-white/40 focus:ring-2 focus:ring-brand-blue"
      />
      <input
        required
        type="email"
        value={form.email}
        onChange={set("email")}
        placeholder="Business email"
        data-testid="report-lead-email"
        className="h-11 w-full border border-white/20 bg-white/5 px-3 text-sm text-white outline-none placeholder:text-white/40 focus:ring-2 focus:ring-brand-blue"
      />
      <button
        type="submit"
        disabled={loading}
        data-testid="report-lead-submit"
        className="inline-flex w-full items-center justify-center gap-2 bg-brand-red px-5 py-3 text-sm font-semibold text-white transition-all hover:-translate-y-px hover:brightness-110 disabled:opacity-60"
      >
        {loading ? "Preparing…" : <>Download Full GCC Report <ArrowRight className="h-4 w-4" /></>}
      </button>
      <p className="text-xs text-white/40">
        We use your details to send the report and occasional relevant updates. Downloading is separate from any marketing subscription.
      </p>
    </form>
  );
};
